/**
 * src/lib/voice/uvtpPoller.js — Plan 93-01 (TICKET-UVTP-POLLER)
 *
 * Client-side poller for the UVTP heavy-path 202 response.
 *
 * `processUvtpScope` already handles the fast path (200) and stamps IDB to
 * 'processing' on the heavy path (202). Without this poller, project
 * recordings >= 24 MB or >= 180 s stall on 'processing' forever once
 * VITE_UVTP_FOR_PROJECTS=1 ships in prod.
 *
 * Server polling endpoint: GET /api/voice-transcribe?runId=<uuid>
 *   Returns { status: 'processing' | 'complete' | 'error', fullTranscript?, ... }
 *
 * Observability contract (Phase 92 spine):
 *   - One logEvent per poll iteration: module='uvtp_poller', step='poll',
 *     status in { 'still_running' | 'ok' | 'error' }, payload: { uvtpRunId, attempt, elapsed_ms }
 *   - Exactly one final logEvent: step='resolve', status in { 'ok' | 'error' | 'timeout' | 'aborted' }
 *   - All rows share the trace_id passed in by the caller (rec.trace_id from voiceQueue).
 */

import { logEvent as defaultLogEvent } from '../observability.js';

const DEFAULT_INTERVALS = Object.freeze({
  initialMs: 2000,
  maxMs: 8000,
  totalBudgetMs: 600_000, // 10 min — heavy path is 24MB+ chunked Whisper
});

export class UvtpPollTimeoutError extends Error {
  constructor(message, uvtpRunId) {
    super(message);
    this.name = 'UvtpPollTimeoutError';
    this.uvtpRunId = uvtpRunId;
  }
}

export class UvtpPollAbortError extends Error {
  constructor(message = 'aborted', uvtpRunId) {
    super(message);
    this.name = 'AbortError';
    this.uvtpRunId = uvtpRunId;
  }
}

/**
 * Poll a UVTP heavy-path run until it resolves, errors, times out, or aborts.
 *
 * @param {object} opts
 * @param {string} opts.uvtpRunId — required; the run id from the 202 response
 * @param {string} opts.traceId — required; the recording's trace_id (Phase 92 spine)
 * @param {AbortSignal} [opts.signal] — optional; aborts the loop when fired
 * @param {object} [opts.intervals] — { initialMs, maxMs, totalBudgetMs }
 * @param {Function} [opts.fetchImpl] — injectable for tests; defaults to globalThis.fetch
 * @param {Function} [opts.logEventImpl] — injectable for tests; defaults to canonical logEvent
 * @param {Function} [opts.sleepImpl] — injectable for tests; defaults to setTimeout-backed sleep
 * @returns {Promise<{ status: 'complete', fullTranscript: string, uvtpRunId: string, [k: string]: any }>}
 */
export async function pollUvtpRun({
  uvtpRunId,
  traceId,
  signal,
  intervals = {},
  fetchImpl = globalThis.fetch && globalThis.fetch.bind(globalThis),
  logEventImpl = defaultLogEvent,
  sleepImpl = defaultSleep,
} = {}) {
  if (!uvtpRunId) throw new Error('[uvtpPoller] uvtpRunId is required');
  if (!traceId) throw new Error('[uvtpPoller] traceId is required');
  if (typeof fetchImpl !== 'function') {
    throw new Error('[uvtpPoller] fetchImpl is not a function (no globalThis.fetch?)');
  }

  const cfg = {
    initialMs: intervals.initialMs ?? DEFAULT_INTERVALS.initialMs,
    maxMs: intervals.maxMs ?? DEFAULT_INTERVALS.maxMs,
    totalBudgetMs: intervals.totalBudgetMs ?? DEFAULT_INTERVALS.totalBudgetMs,
  };

  const start = Date.now();
  let attempt = 0;
  let delay = cfg.initialMs;
  let resolveLogged = false;

  const logPoll = (status, extra = {}) => {
    safeLog(logEventImpl, 'uvtp_poller', 'poll', {
      trace_id: traceId,
      source: 'client',
      module: 'uvtp_poller',
      step: 'poll',
      status,
      payload: { uvtpRunId, attempt, elapsed_ms: Date.now() - start, ...extra },
    });
  };
  const logResolve = (status, extra = {}) => {
    if (resolveLogged) return; // guard against double-fire on abort+timeout race
    resolveLogged = true;
    safeLog(logEventImpl, 'uvtp_poller', 'resolve', {
      trace_id: traceId,
      source: 'client',
      module: 'uvtp_poller',
      step: 'resolve',
      status,
      payload: { uvtpRunId, attempt, elapsed_ms: Date.now() - start, ...extra },
    });
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (signal?.aborted) {
      logResolve('aborted');
      throw new UvtpPollAbortError('aborted', uvtpRunId);
    }
    if (Date.now() - start > cfg.totalBudgetMs) {
      logResolve('timeout');
      throw new UvtpPollTimeoutError(
        `[uvtpPoller] poll budget exceeded for runId=${uvtpRunId} after ${Date.now() - start}ms`,
        uvtpRunId,
      );
    }

    attempt += 1;
    let result;
    try {
      const resp = await fetchImpl(`/api/voice-transcribe?runId=${encodeURIComponent(uvtpRunId)}`, {
        signal,
      });
      // Transient 5xx — log + continue (don't abandon the run on a network blip)
      if (resp && typeof resp.status === 'number' && resp.status >= 500 && resp.status < 600) {
        logPoll('error', { http_status: resp.status, transient: true });
        await sleepImpl(delay, signal);
        delay = Math.min(Math.round(delay * 1.5), cfg.maxMs);
        continue;
      }
      if (!resp || typeof resp.json !== 'function') {
        // Caller injected a non-Response; for tests, allow shape { status, fullTranscript }
        result = resp || {};
      } else {
        result = await resp.json();
      }
    } catch (err) {
      if (err?.name === 'AbortError') {
        logResolve('aborted');
        throw err;
      }
      // Network error — treat as transient
      logPoll('error', { error_message: String(err?.message || err).slice(0, 200), transient: true });
      await sleepImpl(delay, signal);
      delay = Math.min(Math.round(delay * 1.5), cfg.maxMs);
      continue;
    }

    if (signal?.aborted) {
      logResolve('aborted');
      throw new UvtpPollAbortError('aborted', uvtpRunId);
    }

    if (result?.status === 'complete') {
      logPoll('ok');
      logResolve('ok');
      return { ...result, uvtpRunId };
    }
    if (result?.status === 'error' || result?.status === 'failed') {
      const errMsg = result?.error || `UVTP run failed (status=${result?.status})`;
      logPoll('error', { error_message: errMsg });
      logResolve('error', { error_message: errMsg });
      throw new Error(`[uvtpPoller] ${errMsg}`);
    }

    // still_running (server-side status='processing' or 'queued')
    logPoll('still_running', { server_status: result?.status || 'unknown' });

    if (Date.now() - start > cfg.totalBudgetMs) {
      logResolve('timeout');
      throw new UvtpPollTimeoutError(
        `[uvtpPoller] poll budget exceeded for runId=${uvtpRunId} after ${Date.now() - start}ms`,
        uvtpRunId,
      );
    }

    await sleepImpl(delay, signal);
    delay = Math.min(Math.round(delay * 1.5), cfg.maxMs);
  }
}

function defaultSleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new UvtpPollAbortError('aborted'));
    const t = setTimeout(() => {
      if (signal) signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(t);
      reject(new UvtpPollAbortError('aborted'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function safeLog(impl, errorType, message, metadata) {
  try {
    const r = impl(errorType, message, metadata);
    if (r && typeof r.catch === 'function') r.catch(() => {});
  } catch {
    // Logger must never throw out of the poller.
  }
}
